import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import MFormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import React, { ChangeEvent, Fragment, useCallback, useMemo } from 'react';
import { FormGroup, FormGroupProps, Input, InputGroup, InputGroupAddon, InputGroupText, InputProps, Label, UncontrolledTooltip } from 'reactstrap';
import './form_input.scss';
import './form_tagged_input.scss';
import { MentionsInput, Mention, OnChangeHandlerFunc, MentionItem } from 'react-mentions';
import defaultStyle from './formTaggedInputStyles';
import { AutomationVariable } from '@business/entities/automation';

export interface FormTaggedStringInputProps<C extends FormControl<any>> extends FieldProps<C> {
  groupProps?: FormGroupProps;
  withoutGroup?: boolean;
  description?: string;
  container?: string;
  onText?(text: string): void;
  onFocusChange?(focused: boolean): void;
  asterisk?: boolean;
  options: AutomationVariable[];
}

export type FormTaggedInputProps<C extends FormControl<any>> = FormTaggedStringInputProps<C>;

export function FormTaggedInput<C extends FormControl<any>>(props: FormTaggedInputProps<C> & InputProps) {
  const {
    type = 'text',
    readonly,
    withoutGroup = false,
    isInputGroup = false,
    addonText = null,
    onText,
    onlyNumbers = false,
    parseToFloat = false,
    onFocusChange,
    icon,
    inputParentStyles,
    ...rest
  } = props;

  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldValue = R.toString(fieldState.value) || '';
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  const fieldReadonly = fieldConfig.readonly || false;
  const fieldDisabled = props.disabled || fieldConfig.disabled || false;

  const fieldPlaceholder = fieldConfig.placeholder || '';

  const showIcon = useMemo(() => icon && !R.isEmpty(icon), [icon]);

  const handleChange = useCallback(
    (event: { target: { value: string } }, newValue: string, newPlainTextValue: string, mentions: MentionItem[]) => {
      const value: any = event.target.value;

      formControl.setValue(value);
      onText && onText(value);
    },
    [formControl, onText],
  );

  const handleFocus = useCallback(
    event => {
      formControl.onFocus();
      onFocusChange?.(true);
      props.onFocus?.(event);
    },
    [formControl, onFocusChange, props],
  );

  const handleBlur = useCallback(
    event => {
      if (onlyNumbers) {
        formControl.setValue(fieldValue.replace(/\D/g, ''));
      }
      if (parseToFloat) {
        formControl.setValue(fieldValue.replace(/\,/g, ''));
      }
      onFocusChange?.(false);
      formControl.onBlur();
      props.onBlur?.(event);
    },
    [fieldValue, formControl, onFocusChange, onlyNumbers, parseToFloat, props],
  );

  const tooltip = useMemo(() => {
    if (!props.description) return null;
    const uid = formControl.uid.replace('form:', '');
    return (
      <Fragment>
        <i className="fas fa-question-circle ml-2" id={`control-description-${uid}`}></i>
        <UncontrolledTooltip container={props.container} placement="top" target={`control-description-${uid}`}>
          {props.description}
        </UncontrolledTooltip>
      </Fragment>
    );
  }, [formControl.uid, props.container, props.description]);

  let inputContent = (
    <div className="form-input-field" style={{ ...inputParentStyles }}>
      {showIcon && <i className={`${icon} icon`} style={{ ...props.iconStyles }}></i>}
      <MentionsInput singleLine value={fieldValue} onChange={handleChange} placeholder={"Enter value, use '@' for variables"} style={defaultStyle}>
        <Mention
          markup="{__id__}"
          trigger="@"
          data={props.options.map(option => ({
            id: option.value,
            display: option.label,
            namespace: option.namespace,
          }))}
          style={{
            backgroundColor: '#5973F080',
          }}
          renderSuggestion={(suggestion, search, highlightedDisplay) => (
            <div className="mention d-flex justify-content-between">
              <div>{highlightedDisplay}</div>
              <div className="font-size-12 text-muted">{R.get(suggestion, 'namespace')}</div>
            </div>
          )}
        />
      </MentionsInput>
    </div>
  );
  if (isInputGroup && addonText && addonText !== '') {
    inputContent = (
      <InputGroup>
        <InputGroupAddon addonType="prepend">
          <InputGroupText>{addonText}</InputGroupText>
        </InputGroupAddon>
        {inputContent}
      </InputGroup>
    );
  }

  const content = (
    <Fragment>
      {fieldConfig.label && (
        <Label for={formControl.uid} style={!!props.flex ? { display: 'flex', flex: props.flex?.[0] || 1 } : {}}>
          {fieldConfig.label}
          {fieldConfig.required && !R.isEmpty(fieldConfig.label) && (
            <span className="text-danger font-size-14" style={{ marginTop: -5, marginLeft: 2 }}>
              *
            </span>
          )}
          {tooltip}
        </Label>
      )}
      {!!props.flex && (
        <div style={{ flex: props.flex?.[1] || 1, flexDirection: 'row' }}>
          <MFormControl error={!!fieldError} fullWidth>
            {inputContent}
          </MFormControl>
          {!!fieldError && <FormHelperText style={{ color: '#f44336' }}>{fieldError}</FormHelperText>}
        </div>
      )}
      {!props.flex && (
        <MFormControl error={!!fieldError} fullWidth>
          {inputContent}
        </MFormControl>
      )}
      {!props.flex && !!fieldError && <FormHelperText style={{ color: '#f44336' }}>{fieldError}</FormHelperText>}
    </Fragment>
  );

  if (withoutGroup) return content;
  return (
    <FormGroup className={props.flex ? 'd-flex align-items-center' : ''} {...props.groupProps}>
      {content}
    </FormGroup>
  );
}
